// External Collection → Notifications — staff detail view for one sent
// broadcast: the message, resolved audience, email flag, and the
// "X of Y read" stat. (A full per-recipient receipt list can surface
// later; the data is already captured.)

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/design-system/components/ui";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useDateFormatter } from "@/utils/datetime";
import { adminNotificationsApi } from "@/api/adminNotifications";

export function NotificationDetailPage() {
  const { id = "" } = useParams();
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Notifications", href: "/admin/notifications" },
    { label: "Detail" },
  ]);
  const fmt = useDateFormatter();

  const detail = useQuery({
    queryKey: ["admin-notification", id],
    queryFn: () => adminNotificationsApi.get(id),
    enabled: id.length > 0,
  });

  const n = detail.data;

  return (
    <div className="flex flex-col gap-space-4 max-w-2xl">
      <PageHeader
        title={n?.title ?? "Notification"}
        actions={
          <Link to="/admin/notifications" className="text-small text-gold-shadow hover:underline">
            ← Back to notifications
          </Link>
        }
      />

      {detail.isLoading && <div className="text-small text-gray-mid">Loading…</div>}
      {detail.error instanceof Error && (
        <div className="text-small text-error">{detail.error.message}</div>
      )}

      {n && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Message</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-small text-onyx whitespace-pre-wrap">{n.body}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[8rem_1fr] gap-y-space-2 text-small text-onyx">
                <dt className="text-gray-mid">Audience</dt>
                <dd>{n.audienceSummary}</dd>
                <dt className="text-gray-mid">Read</dt>
                <dd>
                  {n.readStats.read} of {n.readStats.total} read
                </dd>
                <dt className="text-gray-mid">Email</dt>
                <dd>
                  {n.emailSent ? (
                    <Badge status="info">Emailed</Badge>
                  ) : (
                    <span className="text-gray-mid italic">In-portal only</span>
                  )}
                </dd>
                <dt className="text-gray-mid">Sent by</dt>
                <dd>{n.senderName ?? "—"}</dd>
                <dt className="text-gray-mid">Sent</dt>
                <dd>{fmt.dateTime(n.sentAt)}</dd>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
